import { isEmpty, isNotEmpty } from 'liyu-pc-base/utils/function';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { ConditionOperatorMap, operator_params_count } from 'liyu-pc-base/api/typing';
import { inject } from 'vue';
import type { TableProState, PivotValueExprToken } from './typing';
import { get_view_settings_for_user } from 'liyu-pc-base/api/view-model';
import { useUserStore, useEnv } from 'liyu-pc-base/init-plugin';
export function removeNestedDuplicates(nodes, seenIds = new Set(), parentPath = []) {
  const result = [];

  nodes.forEach((node, index) => {
    const currentPath = [...parentPath, index]; // 记录当前节点的路径
    const nodeId = node.name;

    if (!seenIds.has(nodeId)) {
      seenIds.add(nodeId);
      const newNode = { ...node };

      if (newNode.children && Array.isArray(newNode.children)) {
        newNode.children = removeNestedDuplicates(newNode.children, seenIds, currentPath);
      }

      result.push(newNode);
    } else {
      // 如果需要处理子对象（即使父对象被忽略了），则递归调用
      if (node.children && Array.isArray(node.children)) {
        // 注意：这里我们不将子对象添加到结果数组中，只是处理它们以避免丢失新的 id
        removeNestedDuplicates(node.children, seenIds, currentPath);
      }
    }
  });

  return result;
}
export const operator_type_map = ConditionOperatorMap;

export const options_for_date_eq = [
  { value: '上月', label: '上月' },
  { value: '本月', label: '本月' },
  { value: '今日', label: '今日' },
  { value: '昨日', label: '昨日' },
  { value: '期初', label: '期初' },
];

export const getParamsCount = (operator, configItem) => {
  return operator_params_count[operator];
};

export const getDefaultSearchOperator = (configItem: any) => {
  if (configItem?.default_operator) {
    return configItem.default_operator;
  }
  if (configItem?.operator_list instanceof Array && configItem.operator_list.length > 0) {
    return configItem.operator_list[0];
  }
  return 'eq';
};

export const filterConditions = conditions => {
  if (!conditions) {
    return [];
  }
  const res = conditions.filter(item => {
    return (
      item.value instanceof Array &&
      item.value.length > 0 &&
      !item.value.some(item => isEmpty(item))
    );
  });
  return res;
};

const processColumnDataSource = (res_data: any, name_map: object) => {
  res_data.forEach((item: any) => {
    name_map[item.name] = item.verbose_name;
    if (item.children) {
      processColumnDataSource(item.children, name_map);
    }
  });
};

export const getColumnNameList = async (modelType: string): Promise<any[]> => {
  if (!modelType) {
    return [];
  }
  const url = modelType.split('-')[0] + '/obtain_' + modelType.split('-')[1] + '_column_data';
  const res = await getApiData({}, url, false, { skip_error: true });
  const name_map = {};
  let res_data = removeNestedDuplicates(res || []);
  processColumnDataSource(res_data, name_map);
  if (!('id' in name_map)) {
    name_map['id'] = 'ID';
  }
  return Object.keys(name_map).map((item: any) => ({
    value: item,
    label: name_map[item],
  }));
};

export const tableProStateSymbol = Symbol('tableProState');

export const useTableProState = (): TableProState => {
  const state = inject<TableProState>(tableProStateSymbol);
  return state;
};
export const fetchViewsData = async (view_key, getQueryParamsFunc, count_url, config) => {
  const userStore = useUserStore();
  const userid = userStore.userid;
  const env = useEnv();
  const res = await get_view_settings_for_user({
    user_id: userid,
    view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
    view_menu__flag: view_key,
    sort_fields: ['sort', 'create_time'],
    is_cache: false,
  });

  const getGroupDataList = async viewData => {
    let selectedConditionGroup = null;
    if (viewData.groupConditionFieldName && config.condition_group_config_list?.length > 0) {
      selectedConditionGroup = config.condition_group_config_list.find(
        item => item.field_name === viewData.groupConditionFieldName,
      );
    } else {
      selectedConditionGroup = null;
    }
    if (!selectedConditionGroup) {
      return [];
    }
    if (!config.query_group_url) {
      return [];
    }

    const params = getQueryParamsFunc({ type: 'group', viewData });
    params['group_condition'] = {
      field_name: selectedConditionGroup.field_name,
      display_name: selectedConditionGroup.display_name,
      order_name: selectedConditionGroup.order_name || [],
    };
    const res = await getApiData(params, config.query_group_url || '', false, {
      api_log: `tablePro ${config.key} ${config.title}`,
    });
    return res || [];
  };

  let list: any = [];
  if (res.system_user_datas) list = list.concat(res.system_user_datas);
  if (res.system_public_datas) list = list.concat(res.system_public_datas);
  if (res.public_datas) list = list.concat(res.public_datas);
  if (res.user_datas) list = list.concat(res.user_datas);

  return Promise.all(
    list.map(async (item: any) => {
      let data = item.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {}
      }
      if (count_url) {
        const groupDataList = await getGroupDataList(data);
        const params = getQueryParamsFunc({ type: 'count', viewData: data, groupDataList });
        const num = await getApiData(params, count_url || '', false, {
          api_log: `tablePro ${config.key} ${config.title}`,
        });
        item.num = num;
      }
      return { ...item, data };
    }),
  );
};

export const getCurrentGroupConditionValue = (conditionGroupDataList, groupConditionValue) => {
  if (!conditionGroupDataList) {
    return null;
  }
  const findItem = conditionGroupDataList.find(
    item => item.group_info.value === groupConditionValue,
  );
  return findItem?.group_info || conditionGroupDataList[0]?.group_info;
};

/**
 * 将 RemoteSelectPro 字段的 value 数组转换为可读 label 字符串
 * 参考 src/components/FormRender/RemoteSelectPro/RemoteSelectPro.vue 的查询逻辑
 * 内置全局缓存：按"单条记录 + valueKey"独立缓存
 *   - 缓存 key 形如 `${postUrl}::${valueKey}::${id}`,valueKey 不同 → 不同缓存
 *   - 命中后不再请求；支持跨调用复用
 *   - 即便某次请求只查 ids=[1,2,3],下次请求 ids=[2,3,4] 也只会再请求 4
 *   - 带 TTL 过期；其他参数(labelKey/labelKeyList 等)不在 key 中,
 *     若需要按字段组合切换建议主动调用 clearRemoteSelectProCache(postUrl)
 *
 * 函数只接收解构后的原始参数,不再依赖某个特定的 configItem 对象,便于在其它场景下复用：
 *   - 入参全部独立命名,未提供的字段走默认值
 *   - value 仅作为入参传入,内部统一处理 Array / 单值 / 空值场景
 *   - 接口请求参数与 RemoteSelectPro 保持完全一致
 *
 * @param value value 数组（RemoteSelectPro 选中项的 id 集合）
 * @param params.RemoteSelectPro 字段配置(任一字段可省略,使用默认值)
 *   - postUrl: 查询接口,必填
 *   - valueKey: id 字段,默认 'id'
 *   - labelKey: 单 label 字段,默认 'name'
 *   - labelKeyList: 多 label 字段数组,默认 []
 *   - labelKeyType: labelKeyList 类型,默认 0
 *   - labelSpace: 多 label 拼接分隔符,默认 '-'
 *   - queryType: 'simple' 走简单查询,其它走高级 conditions
 * @param params.cacheTTL 缓存有效期(毫秒),默认 5 分钟;传 0 表示不缓存
 * @returns label 用逗号拼接的字符串
 */

// 单条记录级缓存：key = `${postUrl}::${valueKey}::${id}` -> { record, timestamp }
// 使用 (globalThis as any) 挂载,保证整个应用单例
const _GLOBAL_CACHE_KEY = '__liyu_pc_base_remote_select_pro_cache__';
type RemoteSelectProCacheEntry = { record: any; timestamp: number };
type RemoteSelectProCacheStore = Record<string, RemoteSelectProCacheEntry>;
const remoteSelectProCache: RemoteSelectProCacheStore = ((globalThis as any)[_GLOBAL_CACHE_KEY] =
  (globalThis as any)[_GLOBAL_CACHE_KEY] || {}) as RemoteSelectProCacheStore;

/**
 * 构造单条记录缓存 key：postUrl + valueKey + id
 */
const buildRecordCacheKey = (postUrl: string, valueKey: string, id: any): string => {
  const idStr = id === null || id === undefined ? '' : String(id);
  return `${postUrl}::${valueKey}::${idStr}`;
};

/**
 * 读取单条缓存；过期则删除并返回 null
 */
const getRecordCache = (
  postUrl: string,
  valueKey: string,
  id: any,
  cacheTTL: number,
): any | null => {
  const key = buildRecordCacheKey(postUrl, valueKey, id);
  const entry = remoteSelectProCache[key];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > cacheTTL) {
    delete remoteSelectProCache[key];
    return null;
  }
  return entry.record;
};

/**
 * 写入单条缓存
 */
const setRecordCache = (postUrl: string, valueKey: string, id: any, record: any): void => {
  const key = buildRecordCacheKey(postUrl, valueKey, id);
  remoteSelectProCache[key] = { record, timestamp: Date.now() };
};

/**
 * 主动清空缓存
 * @param postUrl 指定 postUrl 则只清空该接口的缓存,传空则清空全部
 */
export const clearRemoteSelectProCache = (postUrl?: string): void => {
  if (!postUrl) {
    for (const k of Object.keys(remoteSelectProCache)) {
      delete remoteSelectProCache[k];
    }
    return;
  }
  const prefix = `${postUrl}::`;
  for (const k of Object.keys(remoteSelectProCache)) {
    if (k.startsWith(prefix)) {
      delete remoteSelectProCache[k];
    }
  }
};

export const transferRemoteSelectProValue = async (
  value: any,
  params: {
    postUrl?: string;
    valueKey?: string;
    labelKey?: string;
    labelKeyList?: string[];
    labelKeyType?: number;
    labelSpace?: string;
    queryType?: string;
    cacheTTL?: number;
  } = {},
): Promise<string> => {
  const cacheTTL = params.cacheTTL ?? 5 * 60 * 1000; // 默认 5 分钟
  const postUrl = params.postUrl;
  // 提取 value 数组中的 id
  let ids: any[] = [];
  if (value instanceof Array) {
    ids = value.filter((item: any) => isNotEmpty(item));
  } else if (isNotEmpty(value)) {
    ids = [value];
  }
  if (ids.length === 0) {
    return '';
  }
  if (!postUrl) {
    return ids.join(',');
  }
  const valueKey = params.valueKey || 'id';
  const labelKey = params.labelKey || 'name';
  const labelKeyList: string[] = params.labelKeyList || [];
  const labelKeyType = params.labelKeyType ?? 0;
  const labelSpace = params.labelSpace || '-';
  const queryType = params.queryType;
  // 组装 column_fields 和 sort_fields，参考 RemoteSelectPro 中的 getColumnFields / get_sort_fields
  const columnFields: string[] = [valueKey];
  if (labelKeyList.length > 0 && labelKeyType !== 1) {
    columnFields.push(...labelKeyList);
  } else {
    columnFields.push(labelKey);
  }
  const uniqueFields = [...new Set(columnFields)];
  const sortFields = labelKeyList.length > 0 && labelKeyType !== 1 ? labelKeyList : [labelKey];

  /**
   * 查询一组 ids,返回 id -> record 映射
   * 内部会先从全局缓存批量取,未命中的再发起一次接口请求
   */
  const fetchRecordsByIds = async (targetIds: any[]): Promise<Record<string, any>> => {
    const idMap: Record<string, any> = {};
    if (targetIds.length === 0) return idMap;
    let missingIds: any[] = targetIds;
    if (cacheTTL > 0) {
      const cachedList: any[] = [];
      const missed: any[] = [];
      targetIds.forEach((id: any) => {
        const cached = getRecordCache(postUrl, valueKey, id, cacheTTL);
        if (cached !== null) {
          cachedList.push(cached);
        } else {
          missed.push(id);
        }
      });
      missingIds = missed;
      cachedList.forEach((rec: any) => {
        idMap[String(rec[valueKey])] = rec;
      });
      if (missingIds.length === 0) return idMap;
    }
    let res: any;
    if (queryType === 'simple') {
      res = await getApiData(
        {
          current: 1,
          pageSize: missingIds.length,
          is_pagination: true,
          request_source: 'strict',
          is_active: true,
          column_fields: uniqueFields,
          sort_fields: sortFields,
          [valueKey]: missingIds.join(','),
        },
        postUrl,
        false,
        { skip_error: true },
      );
    } else {
      res = await getApiData(
        {
          current: 1,
          pageSize: missingIds.length,
          is_pagination: true,
          request_source: 'strict',
          is_active: true,
          column_fields: uniqueFields,
          sort_fields: sortFields,
          conditions: [
            [
              {
                class_name: 'int',
                operator: 'in',
                value: missingIds,
                format: 'remote',
                field_name: valueKey,
              },
            ],
          ],
        },
        postUrl,
        false,
        { skip_error: true },
      );
    }
    const dataList: any[] = res?.data || [];
    dataList.forEach((rec: any) => {
      const recId = rec?.[valueKey];
      if (recId === null || recId === undefined) return;
      idMap[String(recId)] = rec;
      if (cacheTTL > 0) {
        setRecordCache(postUrl, valueKey, recId, rec);
      }
    });
    return idMap;
  };

  let idMap: Record<string, any> = {};
  try {
    idMap = await fetchRecordsByIds(ids);
  } catch (e) {
    // 查询失败时回退为原始 value
    return ids.join(',');
  }
  // 按原 ids 顺序匹配，组装 label（与 RemoteSelectPro 中 datasource 的逻辑保持一致）
  const labelList: any[] = [];
  ids.forEach((id: any) => {
    const foundItem: any = idMap[String(id)];
    if (foundItem) {
      if (labelKeyList.length > 0 && labelKeyType !== 1) {
        const labels: any[] = [];
        for (const key of labelKeyList) {
          labels.push(foundItem[key]);
        }
        labelList.push(labels.join(labelSpace));
      } else {
        labelList.push(foundItem[labelKey]);
      }
    } else {
      labelList.push(id);
    }
  });
  return labelList.join(',');
};

export const combineLabel = (labelList: any[], linkType: string) => {
  if (!labelList || labelList.length === 0) return '';
  if (labelList.length === 1) return String(labelList[0]);

  switch (linkType) {
    case '()': {
      const [first, ...rest] = labelList;
      return [first, ...rest.map(item => `(${item})`)].join('');
    }
    case '-':
      return labelList.join('-');
    default:
      return labelList.join('_');
  }
};

/**
 * 值字段「高级表达式 / 过滤条件」合法性校验（统一入口，供编辑器与展示组件复用）。
 *
 * 采用与 Rust 后端一致的递归下降文法校验：
 *   expr      = or ( '?' expr ':' expr )?
 *   or        = and ( '||' and )*
 *   and       = unary ( '&&' unary )*
 *   unary     = '!' unary | comparison
 *   comparison = additive ( REL additive )?        // REL: > < >= <= == !=
 *   additive  = multiplicative ( ('+'|'-') multiplicative )*
 *   multiplicative = power ( ('*'|'/') power )*
 *   power     = primary ( '^' power )*             // 幂，右结合
 *   primary   = 'abs' primary | field | subexpr | value | aggcomputed | number | string | bool | '(' expr ')'
 *              // 'abs' 为一元绝对值前缀：绑定比所有二元运算符更紧，
 *              // 故 abs(x-3) > 1 解析为 (abs(x-3)) > 1，而非 abs((x-3) > 1)
 *              // 'subexpr' 为子表达式引用（value 为子表达式 id），语义上等价于一个操作数，
 *              //   递归展开由后端（pivot_utils._tokens_to_rust）完成，前端校验仅视其为操作数。
 *              // 'value' 为聚合计算值对「聚合后的值字段」的引用（value 为值字段 id），
 *              //   仅出现在聚合计算值表达式中，后端在其聚合结果列上列式求值。
 *              // 'aggcomputed' 为聚合计算值对「其它聚合计算值」的引用（value 为聚合计算值 id），
 *              //   仅出现在聚合计算值表达式中，后端递归内联展开（最多 50 层防环）；
 *              //   传入 options.aggComputedRefs 时还会做引用存在性 / 直接自引用 / 环形依赖校验。
 *
 * 支持字段引用、子表达式引用、聚合值引用、聚合计算值引用、数字、字符串、布尔字面量，
 * 以及 + - * / ^ > < >= <= == != && || ! abs ? : 运算符。
 * 返回 { valid, message }，message 为非法时的中文说明（无需依赖 i18n 词条）。
 */
export interface ValueExprValidation {
  valid: boolean;
  message?: string;
}

/** validateValueExpression 的可选语义校验参数（文法校验之外补充引用完整性校验）。 */
export interface ValueExprValidationOptions {
  // 可引用的聚合计算值列表（aggcomputed token 的 value 须命中其中某 id）；提供时才做
  // 引用存在性 / 环形依赖校验。label 仅用于报错信息展示。
  aggComputedRefs?: { id: string; label: string; expression?: PivotValueExprToken[] }[];
  // 正在编辑的聚合计算值 id：视为「当前表达式所属节点」，用于直接自引用与环形依赖检测
  // （新增时无 id，传入 undefined 即可，此时不会产生环）。
  excludeAggComputedId?: string;
}

export function validateValueExpression(
  tokens?: PivotValueExprToken[] | null,
  options?: ValueExprValidationOptions,
): ValueExprValidation {
  const toks = tokens || [];
  if (toks.length === 0) return { valid: false, message: '表达式为空' };

  const OPERANDS = new Set([
    'field',
    'number',
    'string',
    'bool',
    'subexpr',
    'value',
    'aggcomputed',
  ]);
  const REL = new Set(['>', '<', '>=', '<=', '==', '!=']);
  const isOperand = (t: PivotValueExprToken | null) => !!t && OPERANDS.has(t.type);
  const isOp = (t: PivotValueExprToken | null, v?: string) =>
    !!t && t.type === 'operator' && (v === undefined || t.value === v);

  let pos = 0;
  let error: string | null = null;

  const peek = () => (pos < toks.length ? toks[pos] : null);
  const err = (msg: string) => {
    if (!error) error = msg;
    throw 0;
  };

  const parsePrimary = () => {
    const t = peek();
    if (!t) return err('表达式不完整，缺少操作数');
    if (isOp(t, 'abs')) {
      // 一元绝对值前缀：作用于其后的原子（可嵌套 abs abs x）
      pos++;
      parsePrimary();
      return;
    }
    if (isOperand(t)) {
      pos++;
      return;
    }
    if (t.type === 'lparen') {
      pos++;
      parseExpr();
      const close = peek();
      if (!close || close.type !== 'rparen') return err('括号不匹配，缺少右括号');
      pos++;
      return;
    }
    return err('期望字段、数字、字符串、布尔或左括号');
  };

  const parsePower = () => {
    parsePrimary();
    while (isOp(peek(), '^')) {
      pos++;
      parsePower(); // 右结合
    }
  };
  const parseMultiplicative = () => {
    parsePower();
    while (isOp(peek(), '*') || isOp(peek(), '/')) {
      pos++;
      parsePower();
    }
  };
  const parseAdditive = () => {
    parseMultiplicative();
    while (isOp(peek(), '+') || isOp(peek(), '-')) {
      pos++;
      parseMultiplicative();
    }
  };
  const parseComparison = () => {
    parseAdditive();
    if (isOp(peek()) && REL.has(peek()!.value)) {
      pos++;
      parseAdditive();
    }
  };
  const parseUnary = () => {
    if (isOp(peek(), '!')) {
      pos++;
      parseUnary();
      return;
    }
    parseComparison();
  };
  const parseAnd = () => {
    parseUnary();
    while (isOp(peek(), '&&')) {
      pos++;
      parseUnary();
    }
  };
  const parseOr = () => {
    parseAnd();
    while (isOp(peek(), '||')) {
      pos++;
      parseAnd();
    }
  };
  const parseExpr = () => {
    parseOr();
    if (isOp(peek(), '?')) {
      pos++;
      parseExpr();
      if (!isOp(peek(), ':')) return err('三目运算符缺少 ":"');
      pos++;
      parseExpr();
    }
  };

  try {
    parseExpr();
  } catch {
    return { valid: false, message: error || '表达式非法' };
  }
  if (pos !== toks.length) {
    return { valid: false, message: '表达式存在多余内容或缺少运算符' };
  }
  if (!toks.some(t => OPERANDS.has(t.type))) {
    return { valid: false, message: '表达式至少需要一个操作数' };
  }

  // ===== 语义校验：聚合计算值引用完整性（可选，仅在提供 aggComputedRefs 时执行）=====
  const refs = options?.aggComputedRefs;
  const selfId = options?.excludeAggComputedId;
  if (refs && refs.length > 0) {
    const refById = new Map(refs.map(r => [r.id, r]));

    // 1) 引用存在性 + 直接自引用
    for (const t of toks) {
      if (t.type !== 'aggcomputed') continue;
      if (t.value === selfId) {
        return { valid: false, message: '聚合计算值不能直接引用自身' };
      }
      if (!refById.has(t.value)) {
        return {
          valid: false,
          message: `引用的聚合计算值不存在或已被删除（${t.value}）`,
        };
      }
    }

    // 2) 环形依赖检测：以当前表达式（selfId 节点，deps = 表达式内 aggcomputed 引用）为起点做 DFS，
    //    若沿引用链能回到 selfId 则成环。新增项 selfId 为空 → 不存在环；编辑已有项时
    //    refById 中保留的旧表达式不会被使用（selfId 的 deps 始终取当前表达式），故修改即解环。
    const currentDeps = toks
      .filter(t => t.type === 'aggcomputed' && refById.has(t.value))
      .map(t => t.value);
    const depsOf = (id: string): string[] => {
      if (id === selfId) return currentDeps;
      const r = refById.get(id);
      if (!r) return [];
      return (r.expression || [])
        .filter(t => t.type === 'aggcomputed' && refById.has(t.value))
        .map(t => t.value);
    };
    const visited = new Set<string>();
    const stack = [...currentDeps];
    while (stack.length) {
      const id = stack.pop()!;
      if (id === selfId) {
        return { valid: false, message: '聚合计算值之间存在循环嵌套引用，请先解除引用链' };
      }
      if (visited.has(id)) continue;
      visited.add(id);
      stack.push(...depsOf(id));
    }
  }

  return { valid: true };
}

/** 便捷判断：表达式是否合法（useExpression=true 时调用）。 */
export function isValueExpressionValid(tokens?: PivotValueExprToken[] | null): boolean {
  return validateValueExpression(tokens).valid;
}
